import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useBooking } from "../lib/booking";

const LINKS = [
  { to: "/individual-healing", label: "Individual" },
  { to: "/corporate-solutions", label: "Corporate" },
  { to: "/quick-heals", label: "Quick Heals" },
  { to: "/insights", label: "Insights" },
  { to: "/store", label: "Store" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { openBooking } = useBooking();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-pine/10 bg-cream/85 backdrop-blur-md">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8"
        aria-label="Main navigation"
      >
        <Link to="/" data-testid="nav-logo-link" aria-label="Back of the Mind — home">
          <Logo />
        </Link>

        <div className="hidden items-center gap-6 xl:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`link-underline text-[13px] font-medium tracking-wide ${
                pathname.startsWith(l.to) ? "text-pine" : "text-bodytext/80 hover:text-pine"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-session-button"
            onClick={() => openBooking()}
            className="hidden rounded-full bg-pine px-5 py-2.5 text-[13px] font-semibold text-cream shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-pinedeep hover:shadow-lg sm:block"
          >
            Book a Session
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="rounded-full border border-pine/15 p-2.5 text-pine xl:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-pine/10 bg-cream/95 backdrop-blur-md xl:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block rounded-xl px-3 py-2.5 font-serif text-xl text-ink hover:bg-sagelight/60"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <button
                data-testid="nav-mobile-book-button"
                onClick={() => {
                  setOpen(false);
                  openBooking();
                }}
                className="mt-3 rounded-full bg-pine px-5 py-3 text-sm font-semibold text-cream"
              >
                Book a Session
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
